/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, MouseEvent, useRef} from 'react';
import {makeStyles, MenuList} from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import {translate} from '@ion/core/src/locale/locale';
import {Notifications} from '@ion/core/src/types/entities';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import DropdownItem from '@ion/dls/src/components/menus/DropdownItem';
import SingleSelectMenu from '@ion/dls/src/components/menus/SingleSelectMenu';
import TabView from '@ion/dls/src/components/tabs/TabView';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {MaterialMoreVertIcon} from '@ion/dls/src/icons/MaterialMoreVertIcon';
import Hidden from '@ion/dls/src/layout/Hidden';
import NotificationPanelHeader from '@ion/dls/src/notifications/NotificationPanelHeader';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize, NotificationPanelTabs} from '@ion/dls/src/types/types';

interface NotificationDrawerProps {
  children: JSX.Element;
  closeDrawer: () => void;
  panelOpen?: boolean;
  activeTab: NotificationPanelTabs;
  onTabChange?: (value: {tabHeader: string}) => void;
  tabItems: NotificationTabType[];
  menuItems?: {
    title: string;
    handleClick: (event: MouseEvent<HTMLLIElement>) => void;
    selected?: boolean;
  }[];
  notificationMenuOpen?: boolean;
  setNotificationMenuOpen?: (val: boolean) => void;
  checkboxesEnabled?: boolean;
  selectedNotifications?: Notifications[];
  handleSelectAll?: (event: MouseEvent<HTMLButtonElement>) => void;
  onMarkSelectedAsRead?: () => void;
  onClearSelected?: () => void;
  notificationsCheckAll?: boolean;
}

export type NotificationTabType = {
  tab: NotificationPanelTabs;
  tabHeader: string;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  notificationDrawerRoot : {
    [theme.breakpoints.down('sm')] : {
      zIndex : `${dlsZIndex('notificationDrawerRoot')} !important`
    },
    [theme.breakpoints.up('md')] : {
      '&&' : {
        zIndex : `${dlsZIndex('notificationDrawerRoot')} !important`
      }
    }
  },
  notificationDrawerPaper : {
    width                          : '414px',
    overflow                       : 'hidden',
    [theme.breakpoints.down('sm')] : {
      width  : 'auto',
      height : '100vh'
    }
  },
  notificationMenuRoot : {
    [theme.breakpoints.down('sm')] : {
      zIndex : `${dlsZIndex('notificationDrawerRoot')} !important`
    }
  },
  profileTitle : {
    textTransform : 'uppercase',
    fontWeight    : 500,
    letterSpacing : '1px'
  },
  notificationPanelHeader : {
    display                        : 'flex',
    justifyContent                 : 'space-between',
    alignItems                     : 'center',
    padding                        : '0 24px',
    [theme.breakpoints.down('sm')] : {
      margin : '0 -15px'
    }
  },
  notificationPanelTitle : {
    fontWeight    : 500,
    textTransform : 'uppercase',
    letterSpacing : '1px'
  },
  notificationTabView : {
    margin : '0'
  },
  mobileNotificationsCloseIcon : {
    display : 'flex'
  },
  hiddenMenu : {
    visibility : 'hidden'
  },
  notificationMenuMobilePaper : {
    borderTopLeftRadius  : '8px',
    borderTopRightRadius : '8px',
    padding              : '28px 0'
  },
  closeIcon : {
    marginTop : '5px'
  },
  mobileMenuItem : {
    '&:nth-of-type(3)' : {
      borderBottom : ({menuItems}: {menuItems: any[]}) =>
        menuItems.length > 3 ? `solid 1px ${dlsColor('grey5')}` : 'none'
    }
  }
}));

export const NotificationDrawer = ({
  children,
  panelOpen = false,
  activeTab,
  closeDrawer,
  onTabChange,
  tabItems,
  menuItems,
  notificationMenuOpen,
  setNotificationMenuOpen,
  checkboxesEnabled = false,
  handleSelectAll,
  selectedNotifications = [],
  onMarkSelectedAsRead,
  onClearSelected,
  notificationsCheckAll = false
}: NotificationDrawerProps) => {
  const classes = useStyles({menuItems});
  const {isPhone} = useMedia();
  const moreIconRef = useRef(null);

  function handleTabChange(e: ChangeEvent<{}>, value: number) {
    if (onTabChange) onTabChange(tabItems[value]);
  }

  function onNotificationMenuClick() {
    setNotificationMenuOpen(!notificationMenuOpen);
  }

  function handleMenuClose() {
    setNotificationMenuOpen(false);
  }

  return (
    <Drawer open={panelOpen}
      anchor={isPhone ? 'bottom' : 'right'}
      classes={{
        root  : classes.notificationDrawerRoot,
        paper : classes.notificationDrawerPaper
      }}
      onClose={closeDrawer}>
      <div>
        <div className={classes.notificationPanelHeader}>
          {isPhone &&
          <>
            <div className={classes.mobileNotificationsCloseIcon} onClick={closeDrawer}>
              <MaterialExitCloseIcon size={IconSize.medium} color={dlsColor('grey3')} />
            </div>
            <h2 className={classes.notificationPanelTitle} style={{fontSize: '20px'}}>
              {translate('common:notifications.title')}
            </h2>
          </>}
          {!isPhone &&
          <>
            <div className={classes.closeIcon}>
              <IconButton icon={<MaterialExitCloseIcon size={IconSize.medium} color={dlsColor('grey3')} />}
                onClick={closeDrawer} />
            </div>
            <h2 className={classes.profileTitle}>
              {translate('common:notifications.title')}
            </h2>
          </>
          }
          <div className={!menuItems?.length ? classes.hiddenMenu : null}>
            <div ref={moreIconRef}>
              <FloatingIconButtonGroup.FloatingIconButton onClick={onNotificationMenuClick} color={dlsColor('grey3')}>
                <MaterialMoreVertIcon />
              </FloatingIconButtonGroup.FloatingIconButton>
            </div>
            <Hidden show='desktop'>
              <SingleSelectMenu menuOpen={notificationMenuOpen}
                items={menuItems}
                anchorEl={moreIconRef.current}
                onMenuClose={handleMenuClose} />
            </Hidden>
            <Hidden show='mobile'>
              <Drawer anchor='bottom'
                open={notificationMenuOpen}
                onClose={handleMenuClose}
                ModalProps={{
                  keepMounted : true
                }}
                classes={{
                  paper : classes.notificationMenuMobilePaper,
                  root  : classes.notificationMenuRoot
                }}>
                <MenuList>
                  {
                    menuItems?.map((item, index) => {
                      return (
                        <div key={`single-select-menu-item-${index}`} className={classes.mobileMenuItem}>
                          <DropdownItem text={item.title}
                            onClick={item.handleClick}
                            checked={item.selected} />
                        </div>
                      );
                    })
                  }
                </MenuList>
              </Drawer>
            </Hidden>
          </div>
        </div>
        <ConditionalRender condition={selectedNotifications.length !== 0}>
          <NotificationPanelHeader selectedRows={selectedNotifications}
            count={selectedNotifications.length}
            notificationsCheckAll={notificationsCheckAll}
            onBulkDelete={onClearSelected}
            handleSelectAll={handleSelectAll}
            onBulkAcknowledge={onMarkSelectedAsRead} />
        </ConditionalRender>
        <div className={classes.notificationTabView}>
          <TabView tabItems={tabItems} onChange={handleTabChange} value={activeTab} />
        </div>
        {children}
      </div>
    </Drawer>
  );
};

export default NotificationDrawer;
